import { FC, forwardRef, PropsWithChildren, ReactNode } from "react";
import { Text } from "@otrium/atoms";
import { Box } from "@otrium/core";
import { ChevronRight } from "src/icons/ChevronRight";
import Link from "next/link";

const Separator = () => (
  <Box
    as="span"
    sx={{
      verticalAlign: "top",
      lineHeight: "18px",
      display: "inline-block",
    }}
  >
    <ChevronRight width="20px" height="20px" fill="#ccc" />
  </Box>
);

export type BreadcrumbsItem = {
  name: string | ReactNode; // NOTE: Back button component
  href?: string;
  as?: string;
};

interface BreadcrumbsItemProps {
  item: BreadcrumbsItem;
  isLast: boolean;
  href?: string;
  onClick?: () => void;
}

const BreadcrumbsItem = forwardRef<HTMLLinkElement, BreadcrumbsItemProps>(
  function BreadcrumbsItem({ item, isLast, href, onClick }, ref) {
    return (
      <Box
        data-testid="breadcrumbs-item"
        as="a"
        ref={ref}
        href={href}
        onClick={onClick}
        mr={1}
        color="tone.anthraciteLight"
        sx={{
          display: "inline-block",
          ...(!isLast
            ? {
                ":hover": {
                  color: "#4a4a4a",
                },
              }
            : {}),
        }}
      >
        {item.name}
        {!isLast && <Separator />}
      </Box>
    );
  }
);

interface LinkComponentProps extends PropsWithChildren {
  item: BreadcrumbsItem;
  children: ReactNode;
}

const LinkComponent: FC<LinkComponentProps> = ({ item, children }) => {
  return (
    <Link
      href={item.href as string}
      {...(item.as ? { as: item.as } : {})}
      passHref
      legacyBehavior
    >
      {children}
    </Link>
  );
};

interface BreadcrumbsProps {
  items: BreadcrumbsItem[];
  backButton?: {
    name: string | ReactNode; // NOTE: <Trans /> component
    href: string;
    as?: string;
  };
}

const Breadcrumbs: FC<BreadcrumbsProps> = ({ items, backButton }) => {
  return (
    <Box
      data-testid="breadcrumbs"
      color="#999"
      pb={3}
      mt={5}
      fontSize="14px"
      sx={{
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden",
      }}
      as="nav"
    >
      {backButton && (
        <LinkComponent item={backButton}>
          <Box
            as="a"
            mr={2}
            pr={2}
            color="tone.anthraciteLight"
            sx={{
              display: "inline-block",
              borderRight: "1px solid #d1d1d1",
              ":hover": {
                color: "#4a4a4a",
              },
            }}
          >
            <Text as="span">← </Text>
            <Text as="span" sx={{ textTransform: "capitalize" }}>
              {backButton.name}
            </Text>
          </Box>
        </LinkComponent>
      )}

      {items.map((item, index) => {
        const isLast = items.length - 1 === index;
        return item.href ? (
          <LinkComponent key={index} item={item}>
            <BreadcrumbsItem key={index} item={item} isLast={isLast} />
          </LinkComponent>
        ) : (
          <BreadcrumbsItem key={index} item={item} isLast={isLast} />
        );
      })}
    </Box>
  );
};

export { Breadcrumbs };
